import React, { useState, useMemo, useEffect } from 'react';
import { Euro, ArrowUp, ArrowDown, ArrowUpDown, ShoppingBag, Edit, Building2, Info, Check, X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../../../components/AlertProvider';
import api from '../../../../services/api';
import TierConfigModal from '../../../../components/TierConfigModal';
import { usePricingDisplay } from '../../../../components/PricingRowDisplay';
import { ClientPricing, Client } from './types';
import { getErrorMessage } from './utils';
import BpuDetailedView from './BpuDetailedView';

interface PricingTabProps {
  pricing: ClientPricing[];
  client: Client | null;
  onReload: () => void;
  onClientUpdate?: () => void;
  centralPurchasingName?: string | null;
  centralPurchasingId?: number | null;
}

type PricingMode = 'percentage' | 'fixed' | 'free_units';

interface PricingTier {
  id?: number;
  tier_number: number;
  min_quantity: number;
  max_quantity?: number | null;
  discount_percentage?: number | null;
  free_units?: number | null;
}

interface PricingItem {
  id: number;
  consumable_id: number;
  custom_price: number;
  discount_percentage: number | null;
  free_units?: number | null;
  tiers?: PricingTier[];
  consumable?: {
    title?: string;
    reference?: string;
    base_price?: number;
  } | null;
  is_active?: boolean;
  bpu_id: number;
  bpu_name: string;
  bpu_reference: string;
  bpu_status: string;
}

interface PricingItemPayload {
  consumable_id: number;
  discount_percentage: number | null;
  custom_price: number;
  free_units?: number | null;
  tiers: PricingTier[];
}

const maxDiscountPercent: number = 100;

const parseNumberValue = (value: string): number => {
  const normalized: string = value.replace(',', '.').trim();
  const parsed: number = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const buildPricingPayload = (item: PricingItem): PricingItemPayload => ({
  consumable_id: item.consumable_id,
  discount_percentage: item.discount_percentage,
  custom_price: item.custom_price,
  free_units: item.free_units ?? null,
  tiers: item.tiers || []
});

export const PricingTab: React.FC<PricingTabProps> = ({ pricing, client, onReload, onClientUpdate, centralPurchasingName, centralPurchasingId }) => {
  const { showSuccess, showError } = useAlert();
  const navigate = useNavigate();
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  const isCentralBpuActive = client?.use_central_purchasing_bpu && client?.central_purchasing_id;

  const [editValues, setEditValues] = useState<{
    discount_percentage: string;
    custom_price: string;
    free_units: string;
  }>({
    discount_percentage: '',
    custom_price: '',
    free_units: ''
  });
  const [viewMode, setViewMode] = useState<'card' | 'table' | 'detailed' | 'list'>('detailed');
  const [showTierConfig, setShowTierConfig] = useState(false);
  const [selectedProductForTiers, setSelectedProductForTiers] = useState<number | null>(null);
  const [pricingMode, setPricingMode] = useState<PricingMode>('percentage');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'product', direction: 'asc' });

  // Track which products are included in the BPU (similar to ClientPricingTailwind.tsx)
  const [selectedConsumableIds, setSelectedConsumableIds] = useState<Set<number>>(new Set());

  // Initialize selectedConsumableIds when pricing data loads
  useEffect(() => {
    if (pricing.length > 0) {
      const ids = new Set<number>();
      pricing.forEach(bpu => {
        bpu.pricing_items?.forEach((item: any) => {
          if (item.is_active !== false) {
            ids.add(item.consumable_id);
          }
        });
      });
      setSelectedConsumableIds(ids);
    }
  }, [pricing]);

  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Backend now returns all products with is_active flag
  const allItems = useMemo<PricingItem[]>(() => {
    return pricing.flatMap((bpu) =>
      (bpu.pricing_items || []).map((item) => ({
        ...item,
        bpu_id: bpu.id,
        bpu_name: bpu.name,
        bpu_reference: bpu.reference_number,
        bpu_status: bpu.status
      }))
    );
  }, [pricing]);

  const sortedItems = useMemo(() => allItems.sort((a, b) => {
    if (sortConfig.key === 'product') {
      const nameA = a.consumable?.title || '';
      const nameB = b.consumable?.title || '';
      return sortConfig.direction === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    }
    if (sortConfig.key === 'reference') {
      const refA = a.consumable?.reference || '';
      const refB = b.consumable?.reference || '';
      return sortConfig.direction === 'asc'
        ? refA.localeCompare(refB)
        : refB.localeCompare(refA);
    }
    if (sortConfig.key === 'market_price') {
      const priceA = a.consumable?.base_price || 0;
      const priceB = b.consumable?.base_price || 0;
      return sortConfig.direction === 'asc' ? priceA - priceB : priceB - priceA;
    }
    if (sortConfig.key === 'client_price') {
      const priceA = a.custom_price || 0;
      const priceB = b.custom_price || 0;
      return sortConfig.direction === 'asc' ? priceA - priceB : priceB - priceA;
    }
    return 0;
  }), [allItems, sortConfig]);

  const handleEdit = (item: PricingItem) => {
    setEditingItemId(item.id);

    // Déterminer le mode (Pourcentage ou Montant)
    const isPercentage = !!(item.discount_percentage && item.discount_percentage > 0);
    setPricingMode(isPercentage ? 'percentage' : 'fixed');

    // Calculer le montant de la réduction pour l'initialisation
    let reductionVal = '';
    if (!isPercentage) {
      const base = item.consumable?.base_price || 0;
      const price = item.custom_price || 0;
      // La valeur à éditer est la REDUCTION (Base - Prix), pas le Prix Client
      const reduction = Math.max(0, base - price);
      reductionVal = reduction.toString();
    }

    setEditValues({
      discount_percentage: item.discount_percentage?.toString() || '',
      custom_price: reductionVal,
      free_units: item.free_units?.toString() || ''
    });
  };

  const handleBpuModeChange = async (useCentralBpu: boolean) => {
    if (!client) return;
    try {
      await api.patch(`/clients/${client.id}`, {
        use_central_purchasing_bpu: useCentralBpu
      });
      showSuccess('Mode BPU mis à jour');
      if (onClientUpdate) {
        onClientUpdate();
      }
      onReload();
    } catch (error: any) {
      console.error('Error updating BPU mode:', error);
      showError(getErrorMessage(error));
    }
  };

  const calculateFinalPrice = (item: PricingItem, editing: boolean = false): number | null => {
    const basePrice = item.consumable?.base_price;
    if (!basePrice) return null;

    if (editing) {
      if (pricingMode === 'fixed' && editValues.custom_price) {
        const reductionAmount = parseFloat(editValues.custom_price);
        return basePrice - reductionAmount;
      }

      if (pricingMode === 'percentage' && editValues.discount_percentage) {
        const discount = parseFloat(editValues.discount_percentage);
        return basePrice * (1 - discount / 100);
      }
    }

    return item.custom_price || basePrice;
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!client || editingItemId === null) return;
    if (isCentralBpuActive) {
      showError("Modification interdite : BPU géré par la centrale d'achat");
      return;
    }

    const item = sortedItems.find((entry) => entry.id === editingItemId);
    if (!item) return;

    const basePrice: number = item.consumable?.base_price ?? 0;
    const parsedDiscount: number = Math.min(
      maxDiscountPercent,
      Math.max(0, parseNumberValue(editValues.discount_percentage))
    );
    const parsedReduction: number = Math.max(0, parseNumberValue(editValues.custom_price));
    const parsedFreeUnits: number = Math.max(0, parseNumberValue(editValues.free_units));

    const pricingPayload: PricingItemPayload = (() => {
      if (pricingMode === 'percentage') {
        const discountedPrice: number = Number(
          (basePrice * (1 - parsedDiscount / maxDiscountPercent)).toFixed(2)
        );
        return {
          ...buildPricingPayload(item),
          discount_percentage: parsedDiscount,
          custom_price: discountedPrice,
          free_units: null
        };
      }
      if (pricingMode === 'free_units') {
        return {
          ...buildPricingPayload(item),
          discount_percentage: null,
          custom_price: basePrice,
          free_units: parsedFreeUnits
        };
      }
      const updatedPrice: number = Math.max(0, basePrice - parsedReduction);
      return {
        ...buildPricingPayload(item),
        discount_percentage: null,
        custom_price: Number(updatedPrice.toFixed(2)),
        free_units: null
      };
    })();

    try {
      const allBpuItems: PricingItem[] = allItems.filter((entry) => entry.bpu_id === item.bpu_id);
      const updatedItems: PricingItemPayload[] = allBpuItems.map((bpuItem) =>
        bpuItem.id === item.id ? pricingPayload : buildPricingPayload(bpuItem)
      );

      await api.patch(`/client-pricing/${item.bpu_id}`, {
        pricing_items: updatedItems
      });

      showSuccess('Prix mis à jour');
      setEditingItemId(null);
      onReload();
    } catch (error: unknown) {
      console.error('Error saving price edit:', error);
      showError(getErrorMessage(error));
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
      <div className="p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">Tarifs personnalisés (BPU)</h2>
        </div>

        {/* BPU Source Indicator */}
        {client?.central_purchasing_id && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="flex-shrink-0">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xs sm:text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1 sm:mb-2">
                  Client rattaché à une Centrale d'Achat
                </h3>
                {centralPurchasingName && centralPurchasingId && (
                  <div className="mb-2 sm:mb-3">
                    <label className="block text-xs text-blue-700 dark:text-blue-300 mb-1">Centrale :</label>
                    <button
                      onClick={() => navigate(`/achat/clients/${centralPurchasingId}`)}
                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-100 dark:bg-blue-900/40 border border-blue-300 dark:border-blue-700 rounded hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors group"
                    >
                      <Building2 className="h-3.5 w-3.5 text-blue-700 dark:text-blue-300" />
                      <span className="text-xs font-medium text-blue-900 dark:text-blue-100 group-hover:underline">
                        {centralPurchasingName}
                      </span>
                      <ExternalLink className="h-3 w-3 text-blue-700 dark:text-blue-300" />
                    </button>
                  </div>
                )}
                <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200 mb-2 sm:mb-3">
                  Ce client peut utiliser le BPU de sa centrale ou son propre BPU.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${client.use_central_purchasing_bpu ? 'border-blue-600 bg-blue-600' : 'border-slate-400 bg-white dark:bg-slate-700'}`}>
                      {client.use_central_purchasing_bpu && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <input
                      type="radio"
                      name="bpu_source"
                      checked={client.use_central_purchasing_bpu === true}
                      onChange={() => handleBpuModeChange(true)}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                      Utiliser le BPU de la centrale
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${!client.use_central_purchasing_bpu ? 'border-blue-600 bg-blue-600' : 'border-slate-400 bg-white dark:bg-slate-700'}`}>
                      {!client.use_central_purchasing_bpu && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <input
                      type="radio"
                      name="bpu_source"
                      checked={client.use_central_purchasing_bpu === false}
                      onChange={() => handleBpuModeChange(false)}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                      Utiliser le BPU propre du client
                    </span>
                  </label>
                </div>
                <div className="mt-3 flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {client.use_central_purchasing_bpu
                      ? "Le BPU de la centrale est actuellement appliqué. Les modifications du BPU de la centrale seront automatiquement répercutées."
                      : "Le BPU propre du client est actuellement appliqué. Ce client utilise exclusivement son propre BPU."}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'detailed' && client?.id ? (
          <BpuDetailedView clientId={client.id} />
        ) : sortedItems.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <Euro className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3 sm:mb-4" />
            <p className="text-sm sm:text-base text-gray-500 dark:text-slate-400">Aucun produit disponible</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group"
                    onClick={() => requestSort('product')}
                  >
                    <div className="flex items-center gap-1">
                      Produit
                      {sortConfig.key === 'product' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 text-gray-400 dark:text-slate-500" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group"
                    onClick={() => requestSort('reference')}
                  >
                    <div className="flex items-center gap-1">
                      Référence
                      {sortConfig.key === 'reference' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 text-gray-400 dark:text-slate-500" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group"
                    onClick={() => requestSort('market_price')}
                  >
                    <div className="flex items-center gap-1">
                      Prix Marché
                      {sortConfig.key === 'market_price' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 text-gray-400 dark:text-slate-500" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Mode de réduction
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Paliers
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group"
                    onClick={() => requestSort('client_price')}
                  >
                    <div className="flex items-center gap-1">
                      Prix Client
                      {sortConfig.key === 'client_price' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 text-gray-400 dark:text-slate-500" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                {sortedItems.map((item) => {
                  const isEditing = editingItemId === item.id;

                  // Use pricing display hook for consistent logic
                  const pricingDisplay = usePricingDisplay({
                    basePrice: item.consumable?.base_price || 0,
                    discountPercentage: item.discount_percentage,
                    freeUnits: item.free_units,
                    customPrice: item.custom_price,
                    tiers: item.tiers
                  });

                  const isSelected = selectedConsumableIds.has(item.consumable_id);
                  const isCentralBpuActive = client?.use_central_purchasing_bpu && client?.central_purchasing_id;

                  return (
                    <tr key={item.id} className={`transition-colors ${!isSelected ? 'opacity-50 bg-slate-50 dark:bg-slate-800/50' : isEditing ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${isSelected ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'}`}>
                            <ShoppingBag className="h-4 w-4" />
                          </div>
                          <div>
                            <div className={`text-sm font-medium ${isSelected ? 'text-gray-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'}`}>
                              {item.consumable?.title ?? 'Produit Inconnu'}
                              {!isSelected && <span className="ml-2 text-xs">(Désactivé)</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isSelected ? 'text-gray-500 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500'}`}>
                        {item.consumable?.reference ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                        {typeof item.consumable?.base_price === 'number'
                          ? `${item.consumable.base_price.toFixed(2)} €`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {isEditing ? (
                          <select
                            value={pricingMode}
                            onChange={(e) => setPricingMode(e.target.value as any)}
                            aria-label="Mode de réduction"
                            className="text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 outline-none"
                          >
                            <option value="percentage">% Pourcentage</option>
                            <option value="fixed">€ Montant fixe</option>
                            <option value="free_units">UG Gratuité</option>
                          </select>
                        ) : (
                          <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                            {pricingDisplay.modeDisplay}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => {
                            if (isCentralBpuActive) {
                              setSelectedProductForTiers(item.consumable_id);
                              setShowTierConfig(true);
                              return;
                            }
                            setSelectedProductForTiers(item.consumable_id);
                            setShowTierConfig(true);
                          }}
                          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${isCentralBpuActive
                            ? 'bg-slate-100 text-slate-500 cursor-default'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
                            }`}
                        >
                          {pricingDisplay.tierCount > 0
                            ? `${pricingDisplay.tierCount} palier${pricingDisplay.tierCount > 1 ? 's' : ''}`
                            : (isCentralBpuActive ? '-' : 'Configurer')
                          }
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            {pricingMode === 'percentage' ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={editValues.discount_percentage}
                                  onChange={(e) => setEditValues({ ...editValues, discount_percentage: e.target.value })}
                                  className="w-20 text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 outline-none"
                                  placeholder="%"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                />
                                <span className="text-xs text-slate-500">%</span>
                              </div>
                            ) : pricingMode === 'fixed' ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={editValues.custom_price}
                                  onChange={(e) => setEditValues({ ...editValues, custom_price: e.target.value })}
                                  className="w-24 text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 outline-none"
                                  placeholder="Réduction"
                                  step="0.01"
                                  min="0"
                                />
                                <span className="text-xs text-slate-500">€</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={editValues.free_units}
                                  onChange={(e) => setEditValues({ ...editValues, free_units: e.target.value })}
                                  className="w-20 text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 outline-none"
                                  placeholder="UG"
                                  min="0"
                                />
                                <span className="text-xs text-slate-500">UG</span>
                              </div>
                            )}

                            {/* Preview for % and € */}
                            {pricingMode !== 'free_units' && (
                              <div className="text-[10px] text-slate-400 italic">
                                {calculateFinalPrice(item, true)?.toFixed(2)} €
                              </div>
                            )}
                          </div>
                        ) : pricingDisplay.hasTiers ? (
                          <button
                            onClick={() => {
                              setSelectedProductForTiers(item.consumable_id);
                              setShowTierConfig(true);
                            }}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer"
                          >
                            Voir paliers
                          </button>
                        ) : (
                          <span className={`text-sm font-semibold ${isEditing ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-slate-100'}`}>
                            {pricingDisplay.priceValue} €
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveEdit}
                                className="p-1.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-lg hover:bg-emerald-200 transition-colors"
                                title="Sauvegarder"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setEditingItemId(null)}
                                className="p-1.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 transition-colors"
                                title="Annuler"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(item)}
                                className={`p-1.5 rounded-lg transition-colors ${isCentralBpuActive
                                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400'
                                  }`}
                                disabled={!!isCentralBpuActive}
                                title="Modifier le prix"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (isCentralBpuActive) return;
                                  try {
                                    const isCurrentlySelected = selectedConsumableIds.has(item.consumable_id);

                                    // Toggle selection in the Set
                                    const newSelected = new Set(selectedConsumableIds);
                                    if (isCurrentlySelected) {
                                      newSelected.delete(item.consumable_id);
                                    } else {
                                      newSelected.add(item.consumable_id);
                                    }
                                    setSelectedConsumableIds(newSelected);

                                    // Build pricing items from all selected products
                                    const updatedItems = sortedItems
                                      .filter(i => newSelected.has(i.consumable_id))
                                      .map(i => ({
                                        consumable_id: i.consumable_id,
                                        discount_percentage: i.discount_percentage || 0,
                                        custom_price: i.custom_price || i.consumable?.base_price || 0,
                                        free_units: i.free_units || null,
                                        tiers: i.tiers || []
                                      }));

                                    await api.patch(`/client-pricing/${item.bpu_id}`, {
                                      pricing_items: updatedItems
                                    });

                                    showSuccess(isCurrentlySelected ? 'Produit désactivé' : 'Produit activé');
                                    onReload();
                                  } catch (error: any) {
                                    console.error('Error toggling product:', error);
                                    showError(getErrorMessage(error));
                                  }
                                }}
                                className={`w-10 h-5 rounded-full transition-colors relative ${isCentralBpuActive
                                  ? (selectedConsumableIds.has(item.consumable_id) ? 'bg-emerald-500/50 cursor-not-allowed' : 'bg-slate-200 dark:bg-slate-700 cursor-not-allowed')
                                  : (selectedConsumableIds.has(item.consumable_id) ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400')
                                  }`}
                                title={isCentralBpuActive ? 'Géré par la centrale d\'achat' : (selectedConsumableIds.has(item.consumable_id) ? 'Désactiver ce produit' : 'Activer ce produit')}
                                disabled={!!isCentralBpuActive}
                              >
                                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${selectedConsumableIds.has(item.consumable_id) ? 'left-5' : 'left-0.5'}`} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {allItems.map((item) => {
              const finalPrice = calculateFinalPrice(item, false);
              return (
                <div
                  key={item.id}
                  className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 sm:p-4 flex flex-col gap-2 sm:gap-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                          <ShoppingBag className="h-4 w-4" />
                        </div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-2">
                          {item.consumable?.title ?? 'Produit Inconnu'}
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {item.consumable?.reference ?? '-'}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-slate-500 dark:text-slate-400 text-xs">Prix Marché</div>
                      <div className="font-semibold text-slate-900 dark:text-slate-100">
                        {typeof item.consumable?.base_price === 'number'
                          ? `${item.consumable.base_price.toFixed(2)} €`
                          : '-'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-slate-500 dark:text-slate-400 mb-0.5">Mode de réduction</div>
                      <div className="font-medium text-slate-800 dark:text-slate-200">
                        {item.discount_percentage ? '%' : '€'}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500 dark:text-slate-400 mb-0.5">Réduction</div>
                      <div className="font-medium text-slate-800 dark:text-slate-200">
                        {item.discount_percentage
                          ? `${item.discount_percentage.toFixed(2)}%`
                          : (() => {
                            const base = item.consumable?.base_price || 0;
                            const price = item.custom_price || 0;
                            const reduction = Math.max(0, base - price);
                            return reduction > 0.001 ? `${reduction.toFixed(2)} €` : '-';
                          })()
                        }
                      </div>
                    </div>
                  </div>

                  <div className="mt-1 flex items-center justify-between border-t border-slate-100 dark:border-slate-700 pt-3">
                    <div className="text-xs text-slate-500 dark:text-slate-400">Prix Client</div>
                    <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                      {finalPrice !== null ? `${finalPrice.toFixed(2)} €` : '-'}
                    </div>
                  </div>

                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={() => {
                        if (isCentralBpuActive) return;
                        handleEdit(item);
                        setViewMode('table');
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${isCentralBpuActive
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30'
                        }`}
                      disabled={!!isCentralBpuActive}
                    >
                      {isCentralBpuActive ? (
                        <>
                          <Building2 className="h-3.5 w-3.5" />
                          <span>Centrale</span>
                        </>
                      ) : (
                        <>
                          <Edit className="h-3.5 w-3.5" />
                          <span>Modifier</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tier Configuration Modal */}
      {
        selectedProductForTiers && (
          <TierConfigModal
            isOpen={showTierConfig}
            onClose={() => {
              setShowTierConfig(false);
              setSelectedProductForTiers(null);
            }}
            productName={(() => {
              const item = allItems.find(i => i.consumable_id === selectedProductForTiers);
              return item?.consumable?.title || 'Produit';
            })()}
            basePrice={(() => {
              const item = allItems.find(i => i.consumable_id === selectedProductForTiers);
              return item?.consumable?.base_price || 0;
            })()}
            mode={(() => {
              const item = allItems.find(i => i.consumable_id === selectedProductForTiers);
              // Check which mode has non-null data
              if (item?.free_units !== null && item?.free_units !== undefined) {
                return 'free_units';
              }
              if (item?.discount_percentage !== null && item?.discount_percentage !== undefined) {
                return 'percent';
              }
              // Default to percent if both are null
              return 'percent';
            })()}
            existingTiers={(() => {
              const item = allItems.find(i => i.consumable_id === selectedProductForTiers);
              return (item?.tiers || []).map(tier => ({
                tier_number: tier.tier_number,
                min_quantity: tier.min_quantity,
                max_quantity: tier.max_quantity ?? undefined,
                discount_percentage: tier.discount_percentage ?? undefined,
                free_units: tier.free_units ?? undefined
              }));
            })()}
            onSave={async (tiers, selectedMode) => {
              if (isCentralBpuActive) {
                showError("Modification interdite : BPU géré par la centrale d'achat");
                return;
              }
              try {
                const item = allItems.find(i => i.consumable_id === selectedProductForTiers);
                if (!item || !item.bpu_id) {
                  showError('Impossible de sauvegarder: BPU non trouvé');
                  return;
                }

                // Get all items for this BPU
                const allBpuItems = allItems.filter(i => i.bpu_id === item.bpu_id);

                // Update the pricing items with mode and tiers
                const updatedItems = allBpuItems.map(bpuItem => {
                  if (bpuItem.consumable_id === selectedProductForTiers) {
                    // Apply selected mode and clear opposite mode data
                    if (selectedMode === 'percent') {
                      return {
                        consumable_id: bpuItem.consumable_id,
                        discount_percentage: bpuItem.discount_percentage || 0,
                        custom_price: bpuItem.custom_price,
                        free_units: null,
                        tiers: tiers
                      };
                    } else {
                      return {
                        consumable_id: bpuItem.consumable_id,
                        discount_percentage: null,
                        custom_price: bpuItem.custom_price,
                        free_units: bpuItem.free_units || 0,
                        tiers: tiers
                      };
                    }
                  }
                  return {
                    consumable_id: bpuItem.consumable_id,
                    discount_percentage: bpuItem.discount_percentage,
                    custom_price: bpuItem.custom_price,
                    free_units: bpuItem.free_units,
                    tiers: bpuItem.tiers || []
                  };
                });

                await api.patch(`/client-pricing/${item.bpu_id}`, {
                  pricing_items: updatedItems
                });

                showSuccess('Mode et paliers mis à jour avec succès');
                setShowTierConfig(false);
                setSelectedProductForTiers(null);
                onReload();
              } catch (error: any) {
                console.error('Error updating tiers:', error);
                showError(getErrorMessage(error));
              }
            }}
          />
        )
      }
    </div >
  );
};
